import React from 'react';
import { TableColumn } from '../../../types/search';
import SearchTableColumnSortButton from '../../Search/components/SearchTableColumnSortButton';
import PortalCellCheckbox from './PortalCellCheckbox';

interface DynamicTableProps {
  columns: TableColumn[];
  rows: Record<string, unknown>[];
  onSortChange?: (newSortOrder: string, newSortCol: string) => void;
  sortOrder?: string;
  sortCol?: string;
  listingType: string;
  selectedRows?: string[];
  setSelectedRows?: React.Dispatch<React.SetStateAction<string[]>>;
}

const getRowId = (row: Record<string, unknown>): string => {
  const id = row.id;
  if (typeof id === 'string' || typeof id === 'number') {
    return String(id);
  }
  if (id && typeof id === 'object') {
    try {
      return JSON.stringify(id);
    } catch {
      return '';
    }
  }
  return '';
};

const DynamicTable: React.FC<DynamicTableProps> = ({
  columns,
  rows,
  onSortChange,
  sortOrder,
  sortCol,
  listingType = '',
  selectedRows = [],
  setSelectedRows = () => {},
}) => {
  const allSelected = rows.length > 0 && rows.every(row => selectedRows.includes(getRowId(row)));

  const handleCheckboxChange = (rowId: string) => {
    setSelectedRows(selected =>
      selected.includes(rowId) ? selected.filter(id => id !== rowId) : [...selected, rowId]
    );
  };

  const handleSelectAllChange = () => {
    if (allSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(rows.map(row => getRowId(row)));
    }
  };

  // Helper to compute aria-sort value for a column
  const getAriaSort = (colKey: string, sortCol?: string, sortOrder?: string): 'none' | 'ascending' | 'descending' => {
    if (sortCol === colKey) {
      if (sortOrder === '1') return 'ascending';
      if (sortOrder === '2') return 'descending';
    }
    return 'none';
  };

  const renderHeaderCell = (col: TableColumn) => {
    if (col.key === 'checkbox') {
      return <PortalCellCheckbox isChecked={allSelected} rowNumber={0} onChange={handleSelectAllChange} />;
    }

    if (col.key === 'mobileOnly' || col.key === 'attachments') {
      return <span className="dynamic-table__header-cell-label">{col.label}</span>;
    }

    if (col.screenReaderLabel !== undefined && col.screenReaderLabel !== '') {
      return <span className="sr-only">{col.screenReaderLabel}</span>;
    }

    if (col.key === 'url' || col.key === 'pay') return;

    return (
      <SearchTableColumnSortButton
        columnKey={col.key}
        columnLabel={col.label}
        onSortChange={onSortChange}
        sortCol={sortCol}
        sortOrder={sortCol === col.key ? sortOrder : undefined}
      />
    );
  };

  const renderCell = (
    col: TableColumn,
    row: Record<string, unknown>,
    idx: number,
    isSelected: boolean,
    rowId: string
  ) => {
    if (col.key === 'checkbox') {
      return (
        <PortalCellCheckbox isChecked={isSelected} rowNumber={idx + 1} onChange={() => handleCheckboxChange(rowId)} />
      );
    }

    if (col.customRender) {
      if (col.key === 'mobileOnly') return col.customRender('', row, listingType);
      return col.customRender(row[col.key], row, listingType);
    }
    return JSON.stringify(row[col.key] ?? '');
  };

  return (
    <table className={`dynamic-table dynamic-table--${listingType}`}>
      <thead>
        <tr className={`dynamic-table__header dynamic-table__header--${listingType}`}>
          {columns.map((col, colIdx) => {
            const extensionClass = () => {
              if (col.key === 'checkbox' || col.key === 'jobDate') return ' dynamic-table__header-cell--all-viewports';
              if (col.key === 'mobileOnly') return ' dynamic-table__header-cell--mobile-only';
              return '';
            };

            return (
              <th
                className={`dynamic-table__header-cell dynamic-table__header-cell--${listingType}${extensionClass()}`}
                key={`${col.key}-${colIdx}`}
                scope="col"
                aria-sort={getAriaSort(col.key, sortCol, sortOrder)}
              >
                {renderHeaderCell(col)}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody className="dynamic-table__body">
        {rows.map((row, idx) => {
          const rowId = getRowId(row);
          // Fallback to index if rowId is empty or invalid
          const key = rowId && rowId.trim() !== '' ? rowId : `row-${idx}`;
          const isSelected = selectedRows.includes(rowId);
          const selectedClass = isSelected ? ' dynamic-table__row--selected' : '';
          return (
            <tr
              className={`dynamic-table__row dynamic-table__row--${listingType}${selectedClass}`}
              key={key}
              aria-selected={isSelected}
            >
              {columns.map((col, colIdx) => {
                const mobileOnly = col.key === 'mobileOnly' ? ' dynamic-table__cell--mobile-only' : '';
                return (
                  <td
                    className={`dynamic-table__cell dynamic-table__cell--${listingType}${mobileOnly}`}
                    key={`${rowId}-${col.key}-${colIdx}`}
                  >
                    {renderCell(col, row, idx, isSelected, rowId)}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default DynamicTable;
